import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneInTalk as PhoneInTalkIcon,
} from '@mui/icons-material';
import { ivrClickToCall, ivrMeta } from '../api/ivrApi';
import {
  IVR_DEFAULT_DID,
  getIvrPrefs,
  setIvrPrefs,
  normalizeIvrPhone,
} from '../config/ivr';

function useIvrMeta() {
  const [agents, setAgents] = useState([]);
  const [dids, setDids] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState('');
  const [scoped, setScoped] = useState(false);
  const [did, setDid] = useState(IVR_DEFAULT_DID);
  const [extNo, setExtNo] = useState('');

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setMetaError('');
    const prefs = getIvrPrefs();
    const res = await ivrMeta();
    setLoadingMeta(false);

    if (!res.success) {
      setAgents([]);
      setDids([IVR_DEFAULT_DID]);
      setMetaError(
        res.required_feature
          ? `No access (required: ${res.required_feature})`
          : res.error || 'Failed to load IVR meta'
      );
      return;
    }

    const agentList = res.agents || [];
    setAgents(agentList);
    setScoped(res.scoped === true);

    const didList = res.dids?.length ? res.dids : [res.default_did || IVR_DEFAULT_DID];
    setDids(didList.filter(Boolean));

    const nextDid =
      (prefs.did && didList.includes(prefs.did) ? prefs.did : null) ||
      res.default_did ||
      didList[0] ||
      IVR_DEFAULT_DID;
    setDid(nextDid);

    let nextExt = prefs.ext_no;
    if (agentList.length === 1) {
      nextExt = agentList[0].ext_no;
    } else if (nextExt && !agentList.some((a) => String(a.ext_no) === String(nextExt))) {
      nextExt = agentList[0]?.ext_no || '';
    } else if (!nextExt) {
      nextExt = agentList[0]?.ext_no || '';
    }
    setExtNo(nextExt || '');

    if (!agentList.length) {
      setMetaError(
        res.scoped
          ? 'No IVR agent linked to your phone — ask superadmin'
          : 'No IVR agents available'
      );
    }
  }, []);

  return {
    agents,
    dids,
    loadingMeta,
    metaError,
    scoped,
    did,
    setDid,
    extNo,
    setExtNo,
    loadMeta,
  };
}

/**
 * Click-to-call for a known phone (booking guest / hotel / host).
 */
export default function IvrClickToCall({
  phone,
  label = 'Call',
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  disabled = false,
  context,
  bookingId,
  sx,
}) {
  const [open, setOpen] = useState(false);
  const [calling, setCalling] = useState(false);
  const [targetPhone, setTargetPhone] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const meta = useIvrMeta();

  const normalized = normalizeIvrPhone(phone);
  const canOpen = !disabled && !!normalized && normalized.length >= 10;

  useEffect(() => {
    if (!open) return;
    setTargetPhone(normalized);
    meta.loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, normalized]);

  const handleCall = async () => {
    const phoneNorm = normalizeIvrPhone(targetPhone);
    if (!phoneNorm || phoneNorm.length < 10) {
      setSnackbar({ open: true, message: 'Enter a valid phone number', severity: 'warning' });
      return;
    }
    if (!meta.did || !meta.extNo) {
      setSnackbar({
        open: true,
        message: meta.metaError || 'Select DID and agent extension',
        severity: 'warning',
      });
      return;
    }
    setCalling(true);
    setIvrPrefs({ did: meta.did, ext_no: meta.extNo });
    const res = await ivrClickToCall({
      did: meta.did,
      ext_no: meta.extNo,
      phone: phoneNorm,
      context,
      booking_id: bookingId,
    });
    setCalling(false);
    if (!res.success) {
      setSnackbar({
        open: true,
        message: res.required_feature
          ? `No access (required: ${res.required_feature})`
          : res.error || 'Call failed',
        severity: 'error',
      });
      return;
    }
    setSnackbar({
      open: true,
      message: `Answer your agent phone now — customer will be connected after you pick up${
        res.logid ? ` (log #${res.logid})` : ''
      }`,
      severity: 'success',
    });
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={canOpen ? `Call ${normalized}` : 'No phone number'}>
        <span>
          <Button
            size={size}
            variant={variant}
            color="success"
            startIcon={<PhoneIcon />}
            disabled={!canOpen}
            fullWidth={fullWidth}
            onClick={() => setOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, ...sx }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>

      <IvrCallDialog
        open={open}
        onClose={() => !calling && setOpen(false)}
        title="Click to call"
        meta={meta}
        targetPhone={targetPhone}
        setTargetPhone={setTargetPhone}
        calling={calling}
        onCall={handleCall}
      />

      <CallSnackbar snackbar={snackbar} setSnackbar={setSnackbar} />
    </>
  );
}

/** Header: enter any number and dial */
export function IvrHeaderCallButton({ buttonSx }) {
  const [open, setOpen] = useState(false);
  const [calling, setCalling] = useState(false);
  const [targetPhone, setTargetPhone] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const meta = useIvrMeta();

  const openDialog = () => {
    setTargetPhone('');
    setOpen(true);
    meta.loadMeta();
  };

  const handleCall = async () => {
    const phoneNorm = normalizeIvrPhone(targetPhone);
    if (!phoneNorm || phoneNorm.length < 10) {
      setSnackbar({ open: true, message: 'Enter a valid 10-digit phone', severity: 'warning' });
      return;
    }
    if (!meta.did || !meta.extNo) {
      setSnackbar({
        open: true,
        message: meta.metaError || 'Select DID and agent',
        severity: 'warning',
      });
      return;
    }
    setCalling(true);
    setIvrPrefs({ did: meta.did, ext_no: meta.extNo });
    const res = await ivrClickToCall({
      did: meta.did,
      ext_no: meta.extNo,
      phone: phoneNorm,
      context: 'manual',
    });
    setCalling(false);
    if (!res.success) {
      setSnackbar({
        open: true,
        message: res.required_feature
          ? `No access (required: ${res.required_feature})`
          : res.error || 'Call failed',
        severity: 'error',
      });
      return;
    }
    setSnackbar({
      open: true,
      message: `Answer your agent phone now — customer will be connected after you pick up${
        res.logid ? ` (log #${res.logid})` : ''
      }`,
      severity: 'success',
    });
    setOpen(false);
  };

  return (
    <>
      <Button
        onClick={openDialog}
        startIcon={<PhoneIcon />}
        sx={{
          color: 'white',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.2)' },
          ...buttonSx,
        }}
      >
        Call number
      </Button>

      <IvrCallDialog
        open={open}
        onClose={() => !calling && setOpen(false)}
        title="Manual call"
        meta={meta}
        targetPhone={targetPhone}
        setTargetPhone={setTargetPhone}
        calling={calling}
        onCall={handleCall}
        autoFocusPhone
      />

      <CallSnackbar snackbar={snackbar} setSnackbar={setSnackbar} />
    </>
  );
}

function IvrCallDialog({
  open,
  onClose,
  title,
  meta,
  targetPhone,
  setTargetPhone,
  calling,
  onCall,
  autoFocusPhone,
}) {
  const selectedAgent = meta.agents.find((a) => String(a.ext_no) === String(meta.extNo));
  const singleAgent = meta.agents.length === 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneInTalkIcon color="success" />
        {title}
      </DialogTitle>
      <DialogContent dividers>
        {meta.loadingMeta ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} sx={{ color: '#FF7700' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>
                There is no call UI in the browser
              </Typography>
              <Typography variant="body2" component="div">
                1. Your agent phone rings first
                <br />
                2. Answer on that phone
                <br />
                3. Customer is dialed and bridged
              </Typography>
            </Alert>

            {meta.metaError && (
              <Alert severity="warning">{meta.metaError}</Alert>
            )}

            {selectedAgent?.phone && (
              <Alert severity="success" icon={<PhoneInTalkIcon />}>
                Your handset that will ring:{' '}
                <strong>
                  {selectedAgent.name} · {selectedAgent.phone}
                </strong>
              </Alert>
            )}

            <TextField
              select
              fullWidth
              size="small"
              label="DID (caller ID shown to customer)"
              value={meta.did}
              onChange={(e) => meta.setDid(e.target.value)}
              disabled={!meta.dids.length}
            >
              {(meta.dids.length ? meta.dids : [IVR_DEFAULT_DID]).map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Who answers first (your agent phone)"
              value={meta.extNo}
              onChange={(e) => meta.setExtNo(e.target.value)}
              disabled={!meta.agents.length || singleAgent}
              helperText={
                singleAgent
                  ? 'Linked to your admin phone'
                  : meta.scoped
                    ? 'Only agents matching your phone'
                    : 'Select the agent whose phone you are holding'
              }
            >
              {meta.agents.map((a) => (
                <MenuItem key={a.ext_no} value={a.ext_no}>
                  {a.name} · ext {a.ext_no}
                  {a.phone ? ` · rings ${a.phone}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Customer phone (second leg)"
              value={targetPhone}
              onChange={(e) => setTargetPhone(e.target.value)}
              placeholder="10-digit mobile"
              autoFocus={autoFocusPhone}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={calling}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={calling ? <CircularProgress size={16} color="inherit" /> : <PhoneIcon />}
          onClick={onCall}
          disabled={calling || meta.loadingMeta || !meta.agents.length}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {calling ? 'Ringing your phone…' : 'Ring my phone & connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CallSnackbar({ snackbar, setSnackbar }) {
  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
}
